import type { CreateLegalEntityInput } from "../../types/legalEntity";

/** National digits only: strip non-digits; if 11 digits starting with 1, use last 10 (NANP). */
export function normalizeNationalPhoneDigits(raw: string): string {
  const digits = raw.replace(/\D/g, "");
  if (digits.length === 11 && digits.startsWith("1")) {
    return digits.slice(1);
  }
  return digits;
}

/** Format phone for API payload: +1 plus 10 national digits (e.g. +19839124812). */
export function formatPhoneForApiPayload(raw: string): string {
  const national = normalizeNationalPhoneDigits(raw.trim());
  return `+1${national}`;
}

const LOCAL_PART_ALLOWED = /^[a-zA-Z0-9._%+-]+$/;

/** Unicode letters and ASCII spaces only (no digits, punctuation, or symbols). */
const LEGAL_ENTITY_LETTERS_SPACES_PATTERN = /^[\p{L} ]+$/u;

export const LEGAL_ENTITY_LETTERS_SPACES_ERROR =
  "Use letters and spaces only. Numbers and special characters are not allowed.";

/** Validates name, title, and company (entity name) fields. */
export const getLegalEntityLettersOnlyFieldError = (
  raw: string,
): string | undefined => {
  const t = raw.trim();
  if (t.length === 0) {
    return undefined;
  }
  if (!LEGAL_ENTITY_LETTERS_SPACES_PATTERN.test(t)) {
    return LEGAL_ENTITY_LETTERS_SPACES_ERROR;
  }
  return undefined;
};

export type LegalEntityField = keyof CreateLegalEntityInput;
export type LegalEntityValidationErrors = Partial<
  Record<LegalEntityField, string>
>;

/** Single-field validation for interactive blur/change feedback (phone). FR-003. */
export const getPhoneFieldError = (raw: string): string | undefined => {
  const v = raw.trim();
  if (!v) return "Phone is required.";
  const national = normalizeNationalPhoneDigits(v);
  if (national.length !== 10) return "Enter a valid phone number.";
  return undefined;
};

/** Single-field validation for interactive blur/change feedback (email). FR-004. */
export const getEmailFieldError = (raw: string): string | undefined => {
  const v = raw.trim();
  if (!v) return "Email is required.";
  const at = v.indexOf("@");
  const lastAt = v.lastIndexOf("@");
  if (at <= 0 || at !== lastAt) return "Enter a valid email address.";
  const local = v.slice(0, at);
  const domain = v.slice(at + 1);
  if (!domain.includes(".")) return "Enter a valid email address.";
  const tld = domain.slice(domain.lastIndexOf(".") + 1);
  if (tld.length < 2 || !/^[a-zA-Z]+$/.test(tld))
    return "Enter a valid email address.";
  if (!LOCAL_PART_ALLOWED.test(local)) return "Enter a valid email address.";
  return undefined;
};

const requiredFieldEntries: ReadonlyArray<[LegalEntityField, string]> = [
  ["name", "Name is required."],
  ["title", "Title is required."],
  ["company", "Company is required."],
  ["streetAddress", "Street Address is required."],
  ["city", "City is required."],
  ["state", "State is required."],
  ["zip", "Zip is required."],
];

export const validateLegalEntityInput = (
  input: CreateLegalEntityInput,
): LegalEntityValidationErrors => {
  const errors: LegalEntityValidationErrors = {};

  for (const [field, message] of requiredFieldEntries) {
    if (!input[field].trim()) {
      errors[field] = message;
    }
  }

  const phoneErr = getPhoneFieldError(input.phone);
  if (phoneErr) {
    errors.phone = phoneErr;
  }

  const emailErr = getEmailFieldError(input.email);
  if (emailErr) {
    errors.email = emailErr;
  }

  const nameLetters = getLegalEntityLettersOnlyFieldError(input.name);
  if (nameLetters) {
    errors.name = nameLetters;
  }
  const titleLetters = getLegalEntityLettersOnlyFieldError(input.title);
  if (titleLetters) {
    errors.title = titleLetters;
  }
  const companyLetters = getLegalEntityLettersOnlyFieldError(input.company);
  if (companyLetters) {
    errors.company = companyLetters;
  }

  return errors;
};

const addClientRequiredFieldEntries: ReadonlyArray<[LegalEntityField, string]> =
  [
    ["company", "Company is required."],
    ["streetAddress", "Street Address is required."],
    ["city", "City is required."],
    ["state", "State is required."],
    ["zip", "Zip is required."],
  ];

/** Validates Add Client popup fields only (company + address; no contact fields). */
export const validateAddClientInput = (
  input: CreateLegalEntityInput,
): LegalEntityValidationErrors => {
  const errors: LegalEntityValidationErrors = {};

  for (const [field, message] of addClientRequiredFieldEntries) {
    if (!input[field].trim()) {
      errors[field] = message;
    }
  }

  return errors;
};

/** Validates Add Representative popup: required + phone/email; no letters-only rules. */
export const validateAddRepresentativeInput = (
  input: CreateLegalEntityInput,
): LegalEntityValidationErrors => {
  const errors: LegalEntityValidationErrors = {};

  for (const [field, message] of requiredFieldEntries) {
    if (!input[field].trim()) {
      errors[field] = message;
    }
  }

  const phoneErr = getPhoneFieldError(input.phone);
  if (phoneErr) {
    errors.phone = phoneErr;
  }

  const emailErr = getEmailFieldError(input.email);
  if (emailErr) {
    errors.email = emailErr;
  }

  return errors;
};
